<script lang="ts" setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import type { FormInstance, FormItemInstance } from 'element-plus'
import { useAccountStore, type Account } from '@/stores/setting/Account'
import { rules } from '../ElFormRules/ElFormRuleAccount'
import { storeToRefs } from 'pinia'
import { useActiveOrganization, useAuth } from 'auth-package'

const accountStore = useAccountStore()
const auth = useAuth()
const { userAccount } = storeToRefs(accountStore)

const formAccount = ref<FormInstance>()
const formAccountData = ref<Account>({ ...userAccount.value })
const emailFormItemRef = ref<FormItemInstance>()
const firstNameFormItemRef = ref<FormItemInstance>()
const lastNameFormItemRef = ref<FormItemInstance>()
const activeOrganization = useActiveOrganization()

watch(userAccount, () => {
    formAccountData.value = { ...userAccount.value }

    if (formAccountData.value.attributes === undefined) {
        formAccountData.value.attributes = {}
    }
})

/**
 * Handle click save button
 */
function handleClickSaveButton() {
    formAccount.value?.validate(async (valid: any) => {
        if (valid) {
            try {
                await accountStore.updateUserAccount(formAccountData.value)
                auth.loadUserProfile()
            } catch (error) {
                console.error('Error: When try to update user account', error)
            }
        } else {
            console.error('Account form is not valid!')
        }
    })
}

/**
 * Validate account form
 * It's a helper for disabling save button
 *
 * @returns A boolean (form validation state)
 */
const formAccountIsValid = computed(() => {
    let formIsValid = false
    let fields: any[] = []

    fields.push(
        emailFormItemRef.value?.validateState !== undefined
            ? emailFormItemRef.value?.validateState
            : ''
    )
    fields.push(
        firstNameFormItemRef.value?.validateState !== undefined
            ? firstNameFormItemRef.value?.validateState
            : ''
    )

    fields.push(
        lastNameFormItemRef.value?.validateState !== undefined
            ? lastNameFormItemRef.value?.validateState
            : ''
    )

    const errors = fields.filter((field: any) => {
        return field == 'error'
    })

    formIsValid = Boolean(errors.length)

    return formIsValid
})
</script>

<template>
    <el-form
        ref="formAccount"
        :rules="rules"
        :model="formAccountData"
        label-position="top"
        hide-required-asterisk
    >
        <!--<el-form-item :label="$t('settings.username')">
            <el-input v-model="formAccountData.username" readonly disabled />
        </el-form-item>-->
        <el-form-item ref="emailFormItemRef" :label="$t('settings.email')" prop="email">
            <el-input v-model="formAccountData.email" />
        </el-form-item>
        <el-form-item ref="firstNameFormItemRef" :label="$t('settings.firstName')" prop="firstName">
            <el-input v-model="formAccountData.firstName" />
        </el-form-item>
        <el-form-item ref="lastNameFormItemRef" :label="$t('settings.lastName')" prop="lastName">
            <el-input v-model="formAccountData.lastName" />
        </el-form-item>

        <el-form-item prop="icon">
            <prizific-image-picker
                v-model="formAccountData.attributes.profileImage"
                :title="$t('settings.profileImage')"
                :UUId="activeOrganization.uuid"
                type="organization"
                hidePublicSwitch
            />
        </el-form-item>

        <el-form-item>
            <el-button
                class="mt-3 ms-auto"
                :disabled="formAccountIsValid"
                @click="handleClickSaveButton"
                type="primary"
                rouned
            >
                {{ $t('settings.save') }}
            </el-button>
        </el-form-item>
    </el-form>
</template>

<style lang="scss" scoped></style>
